import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { listAllDestinations, deleteDestination } from "@/lib/destinations.functions";
import { useToast } from "@/hooks/use-toast";
import { RouteErrorBoundary } from "@/lib/route-boundaries";

export const Route = createFileRoute("/admin/destinations/")({
  head: () => ({
    meta: [
      { title: "Destinos | Admin | Fora da Rota" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminDestinationsPage,
  errorComponent: RouteErrorBoundary,
});

function AdminDestinationsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const list = useServerFn(listAllDestinations);
  const del = useServerFn(deleteDestination);
  const [busy, setBusy] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["admin", "destinations"],
    queryFn: () => list({ data: {} as any }),
  });

  const onDelete = async (id: string, name: string) => {
    if (!confirm(`Eliminar destino "${name}"?`)) return;
    setBusy(id);
    try {
      await del({ data: { id } });
      toast({ title: "Destino eliminado" });
      q.refetch();
    } catch (e: any) {
      toast({ title: "Erro", description: e?.message, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Destinos</h1>
          <p className="text-muted-foreground">Páginas de destinos turísticos multi-idioma.</p>
        </div>
        <Button onClick={() => navigate({ to: "/admin/destinations/new" })}>
          <Plus className="mr-2 h-4 w-4" /> Nuevo destino
        </Button>
      </div>

      <div className="rounded-md border bg-card">
        <table className="w-full">
          <thead>
            <tr className="border-b text-left text-sm">
              <th className="p-3">Nombre</th>
              <th className="p-3">Ubicación</th>
              <th className="p-3">Categorías</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Visitas</th>
              <th className="p-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Cargando…</td></tr>
            )}
            {q.data?.length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Sin destinos aún.</td></tr>
            )}
            {q.data?.map((d: any) => (
              <tr key={d.id} className="border-b hover:bg-accent/30">
                <td className="p-3 font-medium">{d.name}</td>
                <td className="p-3 text-sm text-muted-foreground">
                  {[d.state, d.country].filter(Boolean).join(", ")}
                </td>
                <td className="p-3 text-xs">
                  <div className="flex flex-wrap gap-1">
                    {(d.categories ?? []).map((c: string) => (
                      <Badge key={c} variant="secondary">{c}</Badge>
                    ))}
                  </div>
                </td>
                <td className="p-3">
                  <Badge variant={d.status === "published" ? "default" : "outline"}>
                    {d.status}
                  </Badge>
                </td>
                <td className="p-3 text-sm">{d.visit_count ?? 0}</td>
                <td className="p-3 text-right space-x-2">
                  <Button asChild size="sm" variant="outline">
                    <Link to="/admin/destinations/$id" params={{ id: d.id } as any}>
                      <Pencil className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy === d.id}
                    onClick={() => onDelete(d.id, d.name)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
